'use client';

import { useState, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Address, formatUnits, parseUnits } from 'viem';
import { readContract, readContracts } from '@wagmi/core';
import { config } from '@/config/index';
import { ChainType } from '@/constants/chain-specific';
import type { TokenConfigType } from '@/constants/types/currency';
import { DEX_LIST } from '@/constants/project-specific/farms/dex';
import { useNativeTokenHandler } from './useNativeTokenHandler';

// Uniswap V2 Pair ABI (minimal for reserves)
const PAIR_ABI = [{
  inputs: [],
  name: 'getReserves',
  outputs: [{
    name: 'reserve0',
    type: 'uint112'
  }, {
    name: 'reserve1',
    type: 'uint112'
  }, {
    name: 'blockTimestampLast',
    type: 'uint32'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'token0',
  outputs: [{
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'token1',
  outputs: [{
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'totalSupply',
  outputs: [{
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}] as const;

// Factory ABI for getting pair address (standard V2)
const FACTORY_ABI = [{
  inputs: [{
    name: 'tokenA',
    type: 'address'
  }, {
    name: 'tokenB',
    type: 'address'
  }],
  name: 'getPair',
  outputs: [{
    name: 'pair',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}] as const;

// Velodrome-style Factory ABI - getPair has additional 'stable' parameter
const VELODROME_FACTORY_ABI = [{
  inputs: [{
    name: 'tokenA',
    type: 'address'
  }, {
    name: 'tokenB',
    type: 'address'
  }, {
    name: 'stable',
    type: 'bool'
  }],
  name: 'getPair',
  outputs: [{
    name: 'pair',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}] as const;
export interface PoolInfo {
  exists: boolean;
  pairAddress?: Address;
  reserve0: bigint;
  reserve1: bigint;
  token0Address: Address;
  token1Address: Address;
  totalSupply: bigint;
  price0: string; // token1 per token0
  price1: string; // token0 per token1
}
export interface OptimalAmounts {
  amount0: string;
  amount1: string;
  isOptimal: boolean;
  priceImpact: number;
}

/**
 * Hook to get pool information and calculate optimal liquidity ratios
 */
export function usePoolInfo(token0: TokenConfigType | undefined, token1: TokenConfigType | undefined, dexSlug: string, chainId: ChainType = 109) {
  const {
    getEffectiveToken
  } = useNativeTokenHandler(chainId);
  const [userInput, setUserInput] = useState<{
    field: 'token0' | 'token1' | null;
    value: string;
  }>({
    field: null,
    value: ''
  });

  // Get effective tokens (wrapped versions for V2/V3 DEXs)
  const effectiveToken0 = getEffectiveToken(token0, dexSlug);
  const effectiveToken1 = getEffectiveToken(token1, dexSlug);
  const {
    data: poolInfo,
    isLoading,
    error: error_0,
    refetch
  } = useQuery({
    queryKey: ['poolInfo', effectiveToken0?.address, effectiveToken1?.address, dexSlug, chainId],
    queryFn: async (): Promise<PoolInfo | null> => {
      if (!effectiveToken0 || !effectiveToken1) return null;
      try {
        const dex = DEX_LIST[dexSlug];
        if (!dex) throw new Error(`DEX ${dexSlug} not found`);
        console.log(`🔍 Fetching pool info for ${effectiveToken0.symbol}/${effectiveToken1.symbol} on ${dex.name}`);

        // Log if we're using wrapped tokens
        if (effectiveToken0.address !== token0?.address || effectiveToken1.address !== token1?.address) {
          console.log(`🔄 Using wrapped tokens: ${token0?.symbol} -> ${effectiveToken0.symbol}, ${token1?.symbol} -> ${effectiveToken1.symbol}`);
        }

        // Get pair address from factory
        // Velodrome-type DEXes require a 'stable' bool parameter (default false for volatile pairs)
        const isVelodromeType = dex.type === 'velodrome';
        const pairAddress = isVelodromeType ? await readContract(config, {
          address: dex.factory.address as Address,
          abi: VELODROME_FACTORY_ABI,
          functionName: 'getPair',
          args: [effectiveToken0.address as Address, effectiveToken1.address as Address, false],
          // false = volatile pair
          chainId
        }) : await readContract(config, {
          address: dex.factory.address as Address,
          abi: FACTORY_ABI,
          functionName: 'getPair',
          args: [effectiveToken0.address as Address, effectiveToken1.address as Address],
          chainId
        });

        // Check if pair exists (non-zero address)
        if (!pairAddress || pairAddress === '0x0000000000000000000000000000000000000000') {
          console.log('❌ Pool does not exist');
          return {
            exists: false,
            reserve0: 0n,
            reserve1: 0n,
            token0Address: effectiveToken0.address as Address,
            token1Address: effectiveToken1.address as Address,
            totalSupply: 0n,
            price0: '0',
            price1: '0'
          };
        }
        console.log(`✅ Pool found at address: ${pairAddress}`);

        // Get pool data using multicall
        const poolDataCalls = [{
          address: pairAddress,
          abi: PAIR_ABI,
          functionName: 'getReserves' as const
        }, {
          address: pairAddress,
          abi: PAIR_ABI,
          functionName: 'token0' as const
        }, {
          address: pairAddress,
          abi: PAIR_ABI,
          functionName: 'token1' as const
        }, {
          address: pairAddress,
          abi: PAIR_ABI,
          functionName: 'totalSupply' as const
        }];
        const results = await readContracts(config, {
          contracts: poolDataCalls.map(call => ({
            ...call,
            chainId
          }))
        });
        if (results.some(result => result.status === 'failure')) {
          throw new Error('Failed to fetch pool data');
        }
        const [reservesResult, token0Result, token1Result, totalSupplyResult] = results;
        const reserves = reservesResult.result as [bigint, bigint, number];
        const poolToken0 = token0Result.result as Address;
        const poolToken1 = token1Result.result as Address;
        const totalSupply = totalSupplyResult.result as bigint;

        // Determine which token is token0 in the pair
        const isToken0First = effectiveToken0.address.toLowerCase() === poolToken0.toLowerCase();
        const reserve0 = isToken0First ? reserves[0] : reserves[1];
        const reserve1 = isToken0First ? reserves[1] : reserves[0];

        // Calculate prices (how much of token1 per token0 and vice versa)
        // price0 = how many token1 you get per 1 token0 = reserve1 / reserve0
        // We need to adjust for decimal differences between the tokens
        const reserve0Formatted = parseFloat(formatUnits(reserve0, effectiveToken0.decimals));
        const reserve1Formatted = parseFloat(formatUnits(reserve1, effectiveToken1.decimals));
        const price0 = reserve0Formatted > 0 ? (reserve1Formatted / reserve0Formatted).toString() : '0';
        const price1 = reserve1Formatted > 0 ? (reserve0Formatted / reserve1Formatted).toString() : '0';
        console.log(`📊 Pool reserves - ${effectiveToken0.symbol}: ${reserve0Formatted.toFixed(6)}, ${effectiveToken1.symbol}: ${reserve1Formatted.toFixed(6)}`);
        console.log(`💱 Prices - 1 ${effectiveToken0.symbol} = ${parseFloat(price0).toFixed(6)} ${effectiveToken1.symbol}, 1 ${effectiveToken1.symbol} = ${parseFloat(price1).toFixed(6)} ${effectiveToken0.symbol}`);
        return {
          exists: true,
          pairAddress,
          reserve0,
          reserve1,
          token0Address: poolToken0,
          token1Address: poolToken1,
          totalSupply,
          price0,
          price1
        };
      } catch (error) {
        console.error('❌ Error fetching pool info:', error);
        throw error;
      }
    },
    enabled: !!effectiveToken0 && !!effectiveToken1 && !!dexSlug,
    staleTime: 30000,
    // 30 seconds
    retry: 2
  });

  /**
   * Calculate optimal amounts based on current pool ratio
   */
  const calculateOptimalAmounts = useCallback((inputAmount: string, inputField: 'token0' | 'token1'): OptimalAmounts => {
    if (!poolInfo || !poolInfo.exists || !effectiveToken0 || !effectiveToken1) {
      return {
        amount0: inputField === 'token0' ? inputAmount : '',
        amount1: inputField === 'token1' ? inputAmount : '',
        isOptimal: false,
        priceImpact: 0
      };
    }
    const inputAmountParsed = parseFloat(inputAmount || '0');
    if (inputAmountParsed <= 0) {
      return {
        amount0: '',
        amount1: '',
        isOptimal: false,
        priceImpact: 0
      };
    }
    let amount0: string;
    let amount1: string;
    if (inputField === 'token0') {
      amount0 = inputAmount;
      // Calculate amount1 based on pool ratio: amount1 = amount0 * (reserve1 / reserve0)
      const ratio = parseFloat(formatUnits(poolInfo.reserve1, effectiveToken1.decimals)) / parseFloat(formatUnits(poolInfo.reserve0, effectiveToken0.decimals));
      amount1 = (inputAmountParsed * ratio).toFixed(effectiveToken1.decimals);
    } else {
      amount1 = inputAmount;
      // Calculate amount0 based on pool ratio: amount0 = amount1 * (reserve0 / reserve1)
      const ratio_0 = parseFloat(formatUnits(poolInfo.reserve0, effectiveToken0.decimals)) / parseFloat(formatUnits(poolInfo.reserve1, effectiveToken1.decimals));
      amount0 = (inputAmountParsed * ratio_0).toFixed(effectiveToken0.decimals);
    }

    // Calculate price impact (simplified)
    const liquidityAdded0 = parseUnits(amount0, effectiveToken0.decimals);
    const liquidityAdded1 = parseUnits(amount1, effectiveToken1.decimals);
    const priceImpact0 = Number(liquidityAdded0 * 100n) / Number(poolInfo.reserve0);
    const priceImpact1 = Number(liquidityAdded1 * 100n) / Number(poolInfo.reserve1);
    const maxPriceImpact = Math.max(priceImpact0, priceImpact1);
    return {
      amount0,
      amount1,
      isOptimal: true,
      priceImpact: maxPriceImpact
    };
  }, [poolInfo, effectiveToken0, effectiveToken1]);

  /**
   * Update user input and calculate optimal counterpart
   */
  const updateAmount = useCallback((field: 'token0' | 'token1', value: string) => {
    setUserInput({
      field,
      value
    });
  }, []);

  /**
   * Get optimal amounts based on current user input
   */
  const optimalAmounts = calculateOptimalAmounts(userInput.value, userInput.field || 'token0');
  return {
    poolInfo,
    isLoading,
    error: error_0,
    refetch,
    optimalAmounts,
    updateAmount,
    userInput,
    calculateOptimalAmounts
  };
}